/**
 * Presenter
 *
 * @author Dec Norton <decnorton@gmail.com>
 */

import {
    DATE_FORMAT,
    DATE_SHORT_FORMAT,
    readableDateRange,
    TIME_FORMAT,
    TIMESTAMP_FORMAT,
    TIMESTAMP_SAME_YEAR_FORMAT,
} from '@shared/utils/datetime';
// eslint-disable-next-line import/no-cycle
import { get, has, isObject } from 'lodash-es';
import * as moment from 'moment';

let id = 0;

/**
 * @abstract
 */
export class Presenter {
    _data: any;
    $$id: number;

    static DATE_SHORT_FORMAT = DATE_SHORT_FORMAT;
    static DATE_FORMAT = DATE_FORMAT;
    static TIMESTAMP_FORMAT = TIMESTAMP_FORMAT;
    _cache: {};

    constructor(data) {
        this.data = data;
        this.$$id = id++;
    }

    get data() {
        return this._data;
    }

    set data(data) {
        if (!data) {
            throw '[Presenter] Missing data';
        }

        this._data = { ...data };
        delete this._data.$presenter;

        this.invalidateCache();
    }

    get id() {
        return this.data.id;
    }

    /**
     * @param {string} key
     * @param {boolean} isUtc
     * @return {moment.Moment}
     */
    moment(key, isUtc = true): moment.Moment {
        const _moment = this._get(`${key}_moment`, () => {
            const value = this.get(key);

            if (value) {
                return isUtc ? moment.utc(value) : moment(value);
            }

            return value;
        });

        if (moment.isMoment(_moment)) {
            return _moment.clone();
        }

        return _moment;
    }

    get createdAt() {
        return this.formatTimestamp(this.createdAtMoment);
    }

    get createdAtDate() {
        return this._get('createdAtDate', () => this.formatDate(this.createdAtMoment));
    }

    get createdAtTime() {
        return this._get('createdAtTime', () => this.formatTime(this.createdAtMoment));
    }

    get createdAtMoment() {
        return this.moment('created_at', true);
    }

    get updatedAt() {
        return this._get('updatedAt', () => {
            return this.updatedAtMoment.local().format(Presenter.TIMESTAMP_FORMAT);
        });
    }

    get updatedAtMoment() {
        return this.moment('updated_at', true);
    }

    get deletedAt() {
        return this._get('deletedAt', () => {
            return this.deletedAtMoment.local().format(Presenter.TIMESTAMP_FORMAT);
        });
    }

    get deletedAtMoment() {
        return this._get('deletedAtMoment', () => {
            return moment.utc(this.data.deleted_at);
        }).clone();
    }

    static detach(model) {
        if (isObject(model) && model.$presenter) {
            delete model.$presenter;
        }

        return model;
    }

    static attach(model, updateData = true) {
        if (!model) {
            return model;
        }

        if (Array.isArray(model)) {
            return model.map(m => {
                return this.attach(m, updateData);
            });
        }

        if (isObject(model)) {
            if (model.$presenter === null || !(model.$presenter instanceof this)) {
                // Doesn't have a presenter yet
                model.$presenter = new this(model);
            } else if (updateData) {
                model.$presenter.data = model;
            }
        }

        return model;
    }

    /**
     * @param {object} model
     * @return {Presenter|Presenter[]}
     */
    static create(model) {
        model = this.attach(model);

        if (Array.isArray(model)) {
            return model.map(m => m.$presenter);
        }

        return model ? model.$presenter : undefined;
    }

    invalidateCache() {
        // Invalidate cache
        this._cache = {};
    }

    _has(path) {
        return has(this.data, path);
    }

    has(path) {
        return this._has(path);
    }

    get(path, defaultValue = undefined) {
        return get(this.data, path, defaultValue);
    }

    _get(key, getter) {
        if (this._cache.hasOwnProperty(key)) {
            return this._cache[key];
        }

        const value = getter();
        this._cache[key] = value;
        return value;
    }

    /**
     * @param {moment.Moment} value
     * @param {boolean} local
     * @return {?string}
     */
    formatTimestamp(value, local = true) {
        if (!value) {
            return null;
        }

        let format = TIMESTAMP_FORMAT;

        if (value.isSame(moment(), 'year')) {
            format = TIMESTAMP_SAME_YEAR_FORMAT;
        }

        return local ? value.local().format(format) : value.format(format);
    }

    /**
     * @param {moment.Moment} value
     * @param {boolean} local
     * @return {?string}
     */
    formatDate(value, local = true) {
        if (!value) {
            return;
        }

        return local ? value.local().format(DATE_FORMAT) : value.format(DATE_FORMAT);
    }

    formatDateRange(start, finish) {
        return readableDateRange(start, finish);
    }

    /**
     * @param {Moment} value
     * @param {boolean} local
     * @return {?string}
     */
    formatTime(value, local = true) {
        if (!value) {
            return;
        }

        return local ? value.local().format(TIME_FORMAT) : value.format(TIME_FORMAT);
    }

    /**
     * @param {moment.Moment} value
     * @param {boolean} local
     * @return {?string}
     */
    formatShortDate(value, local = true) {
        if (!value) {
            return;
        }

        return local ? value.local().format(DATE_SHORT_FORMAT) : value.format(DATE_SHORT_FORMAT);
    }

    /**
     * Custom JSON serialisation.
     * @return {null}
     */
    toJSON() {
        // We don't want to serialize anything in this class as we might get cyclical references.
        return undefined;
    }
}

export default Presenter;
